import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { useTranslation } from '../../../../i18n'

import SBox from 'components/opinionless/SBox'

import FCardSelect from 'components/BaseComponents/FCardSelect'

import FButton from 'components/BaseComponents/FButton'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { defaultMargin } from 'components/BaseComponents/constants'
import { LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { ga4_useTitle } from 'utils/ga4'
import { BenefitSelectedInputType, useBenefitsSelectionMutation } from '../../../../types/graphql'

import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

import FInputError from 'components/BaseComponents/FInputError'

interface Props {
    onAnswer: (props) => void
    selectedBenefits: string[]
    personId: any
    setIsBackwardsStep?: any
    updatePercentage: any
}

const BenefitsSelection: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const form = useForm()
    const { errors, setError, clearErrors } = form

    const [benefitsSelectionMutation, { data: selectBenefitsResponse, loading }] = useBenefitsSelectionMutation()

    const { t } = useTranslation(window.LANG_NS)

    useEffect(() => {
        form.setValue('benefits_selection', props.selectedBenefits || [])
    }, [props.selectedBenefits])

    ga4_useTitle('Selection')

    const benefits = useMemo(() => {
        return [
            {
                text: 'Supplemental Security Income',
                id: 'SSI',
                imageUrl: 'https://storage.googleapis.com/portal-web-resources/Default%20Website/BenefitsIcons/SSI.svg',
                checked: props.selectedBenefits.includes('SSI'),
            },
            {
                text: 'CalFresh',
                id: 'CAL_FRESH',
                imageUrl:
                    'https://storage.googleapis.com/portal-web-resources/Default%20Website/BenefitsIcons/CalFresh.svg',
                checked: props.selectedBenefits.includes('CAL_FRESH'),
            },
            {
                text: 'CalWORKs',
                id: 'CAL_WORKS',
                imageUrl:
                    'https://storage.googleapis.com/portal-web-resources/Default%20Website/BenefitsIcons/Calworks.svg',
                checked: props.selectedBenefits.includes('CAL_WORKS'),
            },
            {
                text: 'Head Start',
                id: 'HEAD_START',
                imageUrl:
                    'https://storage.googleapis.com/portal-web-resources/Default%20Website/BenefitsIcons/Headstart.svg',
                checked: props.selectedBenefits.includes('HEAD_START'),
            },
            {
                text: 'Affordable Connectivity Program',
                id: 'AFFORDABLE_CONNECTIVITY',
                imageUrl: 'https://storage.googleapis.com/portal-web-resources/Default%20Website/BenefitsIcons/ACP.svg',
                checked: props.selectedBenefits.includes('AFFORDABLE_CONNECTIVITY'),
            },
            {
                text: 'Housing',
                id: 'HUD_HOUSING',
                imageUrl:
                    'https://storage.googleapis.com/portal-web-resources/Default%20Website/BenefitsIcons/Housing.svg',
                checked: props.selectedBenefits.includes('HUD_HOUSING'),
            },
        ]
    }, [t])

    const onContinue = async (values) => {
        const hasError = values.benefits_selection.length < 1
        if (hasError) {
            handleCardSelectError(hasError)
            return
        }

        props.onAnswer(values.benefits_selection || ['NONE'])
        const payload: BenefitSelectedInputType[] = values.benefits_selection.map((benefit: string) => {
            return {
                personId: props.personId,
                category: benefit,
            }
        })

        await benefitsSelectionMutation({ variables: { benefits: payload } })
        props.setIsBackwardsStep(false)
        props.nextStep()
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.benefits,
        WIZARD_STEPS_MAPPING.benefits.selection,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    const handleCardSelectError = (hasError: boolean) => {
        if (hasError) {
            setError('benefits_selection', { type: 'custom', message: 'required_benefits_selection' })
        } else {
            clearErrors('benefits_selection')
        }
    }

    return (
        <FormProvider {...form}>
            <SBox as="form" onSubmit={form.handleSubmit(onContinue)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBTextSubHeader
                    dangerouslySetInnerHTML={{
                        __html: t('ssi_benefits_wizard_benefits_receive'),
                    }}
                />
                <FCardSelect
                    cardDataArray={benefits}
                    noneOptionLabel={t('ssi_i_do_not_receive')}
                    noneChecked={props.selectedBenefits.includes('NONE')}
                    id={'benefits_selection'}
                    name={'benefits_selection'}
                    minimumSelection={1}
                />
                {errors.benefits_selection && (
                    <FInputError id={`benefits_selection_error`} forElementId={'benefits_selection'}>
                        {t(errors['benefits_selection']?.message)}
                    </FInputError>
                )}
                <FButton
                    tabIndex={0}
                    type={'submit'}
                    loading={loading}
                    title={t('continue')}
                    width={'100%'}
                    sx={{ marginTop: defaultMargin }}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('save_and_continue')}
                </FButton>
            </SBox>
        </FormProvider>
    )
}

export default BenefitsSelection
